import React, { useEffect } from 'react';
import { Chrome, Search, Users, Zap, BriefcaseIcon, Filter, Clock, Linkedin } from 'lucide-react';
import { YouTubePlayer } from './components/YouTubePlayer';
import { Link } from 'react-router-dom';

export default function App() {
  useEffect(() => {
    createStars();
    return () => {
      const stars = document.querySelector('.stars');
      if (stars) stars.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative">
      {/* Background Effects */}
      <div className="fixed inset-0 green-gradient opacity-50"></div>
      <div className="fixed top-20 left-1/4 w-[500px] h-[500px] bg-[#00FF9D]/5 rounded-full blur-[100px]"></div>
      <div className="fixed bottom-20 right-1/4 w-[500px] h-[500px] bg-[#00FF9D]/5 rounded-full blur-[100px]"></div>

      {/* Navbar */}
      <nav className="relative z-10 px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img src="/planete-annelee.png" alt="Tarss" className="w-8 h-8" />
            <span className="font-bold text-xl sm:text-2xl mono">Tarss</span>
          </div>
          <div className="flex gap-3 sm:gap-6 items-center">
            {/* <a href="https://discord.com/invite/TFDj2MJHMZ" target="_blank" rel="noopener noreferrer" className="secondary-button flex items-center gap-2 px-4 sm:px-8 text-sm sm:text-base">
              <img src="/discord.png" alt="Discord" className="w-4 sm:w-5 h-4 sm:h-5" />
              Discord
            </a> */}
            <a href="https://chromewebstore.google.com/detail/tarss-extension/cgdjipjplklipnlfbglgkjlnhfkpihfd?hl=fr" target="_blank" rel="noopener noreferrer" className="primary-button flex items-center gap-2 px-4 sm:px-8 text-sm sm:text-base">
              <Chrome className="w-4 sm:w-5 h-4 sm:h-5" />
              Télécharger
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 sm:pt-40 pb-24 sm:pb-48 px-4 sm:px-6">
        <div className="max-w-[1400px] mx-auto text-center">
          <h1 className="text-4xl text-[#00FF9D] sm:text-6xl font-bold mb-10 sm:mb-20 glow-text tracking-tight mono">
            Ne ratez plus aucune mission freelance sur LinkedIn
          </h1>
          <h2 className="text-lg sm:text-xl mb-10 sm:mb-20 max-w-3xl mx-auto text-gray-400/90 leading-relaxed px-4">
            L'extension Chrome qui filtre automatiquement votre feed LinkedIn pour dénicher les meilleures offres de mission pour les développeurs et freelances tech.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center mb-16 sm:mb-32 px-4">
            <a href="https://chromewebstore.google.com/detail/tarss-extension/cgdjipjplklipnlfbglgkjlnhfkpihfd?hl=fr" 
               target="_blank" 
               rel="noopener noreferrer" 
               className="primary-button flex items-center justify-center gap-3 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base">
              <Chrome className="w-4 sm:w-5 h-4 sm:h-5" />
              Ajouter Tarss à Chrome – Gratuit
            </a>
            {/* <a href="https://discord.com/invite/TFDj2MJHMZ"
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-button flex items-center justify-center gap-3 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base">
              <img src="/discord.png" alt="Discord" className="w-4 sm:w-5 h-4 sm:h-5" />
              Rejoindre le Discord
            </a> */}
          </div>
        </div>

        {/* Extension Preview */}
        <div className="max-w-[1400px] mx-auto px-4">
          <div className="glass-card rounded-xl p-4 sm:p-8 glow-border">
            <img 
              src="/screenShot.png" 
              alt="Tarss Extension Preview"
              className="w-full rounded-lg shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center mono text-[#00FF9D]">
            Trouvez plus de missions, plus facilement
          </h2>
          
          <p className="text-lg text-gray-400/90 leading-relaxed max-w-3xl mx-auto mb-12 text-center">
            De nombreuses offres de mission sont publiées sur LinkedIn sans apparaître sur la page Offres d'emploi, ce qui rend leur découverte difficile pour les freelances. Tarss automatise votre veille et vous assure de ne passer à côté d'aucune opportunité.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Search className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Trouver les missions pertinentes rapidement</h3>
              <p className="text-gray-400">Tarss repère pour vous les offres correspondant à vos compétences dès qu'elles apparaissent sur le feed LinkedIn.</p>
            </div>

            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Ne plus rater d'opportunités</h3>
              <p className="text-gray-400">Même si vous n'êtes pas connecté en permanence, chaque nouvelle mission détectée est enregistrée, vous pouvez la consulter plus tard.</p>
            </div>

            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Gagner un temps précieux</h3>
              <p className="text-gray-400">Fini le scroll sans fin sur LinkedIn : Tarss centralise tout sur une interface de veille, ce qui vous libère du temps pour vos projets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative z-10 py-20 px-6">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center mono text-[#00FF9D]">
            Comment Tarss trouve des missions sur LinkedIn
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Search className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Analyse automatique de votre feed</h3>
              <p className="text-gray-400">Une fois l'extension installée, Tarss parcourt silencieusement votre fil d'actualités LinkedIn et détecte les nouvelles offres de mission correspondant à vos critères.</p>
            </div>

            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Centralisation des missions</h3>
              <p className="text-gray-400">Les missions détectées sont enregistrées dans votre interface Tarss. Vous accédez en un clin d'œil à toutes les offres repérées sur votre feed, ainsi qu'à celles trouvées par la communauté Tarss.</p>
            </div>

            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Filter className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Filtrage et recherche avancée</h3>
              <p className="text-gray-400">Tarss vous permet de filtrer ces missions par mots-clés (compétence, localisation, etc.) et d'effectuer des recherches dans cet historique. Vous pouvez par exemple n'afficher que les missions de développeur Java à Paris.</p>
            </div>

            <div className="glass-card rounded-xl p-6 sm:p-8">
              <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-[#00FF9D]" />
              </div>
              <h3 className="text-xl font-bold mb-4 mono">Notifications et historique</h3>
              <p className="text-gray-400">Recevez des alertes quand de nouvelles missions apparaissent, et consultez à tout moment l'historique complet de toutes les missions passées dans votre feed. Tarss garde en mémoire ce qui est passé, pour que vous ne perdiez aucune information.</p>
            </div>
          </div>

          {/* <div className="mt-12 max-w-4xl mx-auto">
            <div className="glass-card rounded-xl p-4 sm:p-8 glow-border">
              <img 
                src="/screenShot.png" 
                alt="Interface Tarss listant des offres de mission freelance détectées sur LinkedIn"
                className="w-full rounded-lg shadow-xl"
              />
            </div>
          </div> */}
        </div>
      </section>

      {/* Why Tarss Section */}
{/*       <section className="relative z-10 py-20 px-6">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center mono">Pourquoi Tarss existe ?</h2>
          
            <p className="text-xl text-gray-400/90 leading-relaxed max-w-[1200px] mx-auto">
              Le flux LinkedIn est une mine d'or d'opportunités qui n'apparaissent jamais dans la section emploi. Mais ces publications disparaissent rapidement, ensevelies sous les nouveaux contenus. Nous avons automatisé le processus de capture de ces opportunités précieuses mais éphémères, transformant votre flux passif en un tableau de bord actionnable.
            </p>
          
        </div>
      </section> */}

      {/* Stats Section */}
      <section className="relative z-10 py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 bg-[#00FF9D]/[0.02] border border-[#00FF9D]/10 rounded-xl p-6">
            <div className="text-center">
              <h3 className="text-3xl sm:text-5xl font-bold mb-2 text-[#00FF9D] mono">+1100</h3>
              <p className="text-sm sm:text-base text-gray-400">utilisateurs partagent leurs opportunités</p>
            </div>
            <div className="text-center">
              <h3 className="text-3xl sm:text-5xl font-bold mb-2 text-[#00FF9D] mono">0</h3>
              <p className="text-sm sm:text-base text-gray-400">Configuration Requise</p>
            </div>
            <div className="text-center">
              <h3 className="text-3xl sm:text-5xl font-bold mb-2 text-[#00FF9D] mono">Fremium</h3>
              <p className="text-sm sm:text-base text-gray-400">Démarrage gratuit</p>
            </div>
            <div className="text-center">
              <h3 className="text-3xl sm:text-5xl font-bold mb-2 text-[#00FF9D] mono">2min</h3>
              <p className="text-sm sm:text-base text-gray-400">Pour Démarrer</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 py-12 sm:py-16 px-4">
        <div className="max-w-[1400px] mx-auto">
          {/* Freelances Section */}
          <div className="mb-24 sm:mb-48">
            <h2 className="text-3xl sm:text-4xl font-bold mono text-center mb-6 sm:mb-8">
              <span className="text-[#00FF9D]">Freelances</span>
            </h2>
            <div className="rounded-xl p-4 sm:p-8 bg-[#00FF9D]/[0.02] border border-[#00FF9D]/10">
              <p className="text-base sm:text-lg text-gray-400/90 leading-relaxed mb-6 sm:mb-8">
                Votre prochain projet de rêve ne devrait pas dépendre de votre présence en ligne au bon moment. Accédez aux missions partagées par toute la communauté Tarss, associées automatiquement à vos compétences.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-8 sm:mb-12">
                <div className="feature-card glass-card rounded-xl p-6 sm:p-8">
                  <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                    <Users className="w-6 h-6 text-[#00FF9D]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-4 mono">MatchMaking Direct</h3>
                  <p className="text-gray-400">Trouvez directement depuis une mission ou une candidature la personne ou l'offre parfaite</p>
                </div>
                <div className="feature-card glass-card rounded-xl p-6 sm:p-8">
                  <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                    <Search className="w-6 h-6 text-[#00FF9D]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-4 mono">Évaluation Recruteurs</h3>
                  <p className="text-gray-400">Recherchez et laissez un avis aux recruteurs pour trouver le contact idéal</p>
                </div>
                <div className="feature-card glass-card rounded-xl p-6 sm:p-8">
                  <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                    <Zap className="w-6 h-6 text-[#00FF9D]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-4 mono">Alertes Intelligentes</h3>
                  <p className="text-gray-400">Soyez alertés dès que vos mots-clés sont trouvés dans un post LinkedIn. Idéal pour suivre un concurrent, un client ou une tendance</p>
                </div>
                <div className="feature-card glass-card rounded-xl p-6 sm:p-8">
                  <div className="w-12 h-12 bg-[#00FF9D]/10 rounded-lg flex items-center justify-center mb-6">
                    <Users className="w-6 h-6 text-[#00FF9D]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-4 mono">Network (Bêta)</h3>
                  <p className="text-gray-400">Visualisez les personnes de votre réseau dans un graph. Une nouvelle façon de comprendre vos connexions</p>
                </div>
              </div>
              <div className="max-w-2xl mx-auto">
                <YouTubePlayer 
                  videoId="lpUHaFHrWTg"
                  title="Tarss pour les Freelances"
                />
              </div>
            </div>
          </div>


        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative z-10 py-32 px-6">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8 mono text-[#00FF9D]">
            Tarifs – Gratuit vs Tarss+
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            {/* Free Tier */}
            <div className="glass-card rounded-xl p-8 relative">
              <h3 className="text-2xl font-bold mb-6 mono">Tarss Gratuit</h3>
              <p className="text-gray-400 mb-8">Pour commencer sans frais</p>
              
              <ul className="space-y-4 text-left mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Accès aux offres de plus de 48h</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Matchmaking sur les compétences de votre profil</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Chercher des recruteurs et les évaluer</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Chercher des candidatures spontanées</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Alertes : 3 mots-clés maximum et 10 posts trouvés par jour</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Matchmaking candidatures et missions : 3 résultats maximum</span>
                </li>
              </ul>

              <a href="https://chromewebstore.google.com/detail/tarss-extension/cgdjipjplklipnlfbglgkjlnhfkpihfd?hl=fr" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="primary-button w-full flex items-center justify-center gap-2 px-6 py-3 text-base">
                <Chrome className="w-5 h-5" />
                Commencer gratuitement
              </a>
            </div>

            {/* Pro Tier */}
            <div className="glass-card rounded-xl p-8 relative border-2 border-[#00FF9D]/20">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#00FF9D] text-black px-4 py-1 rounded-full text-sm font-bold">
                Le plus populaire
              </div>
              
              <h3 className="text-2xl font-bold mb-6 mono">Tarss+</h3>
              <p className="text-gray-400 mb-8">9.90€/mois</p>
              
              <ul className="space-y-4 text-left mb-8">
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Accès à toutes les offres</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Matchmaking : possibilité d'exclure des compétences aux recherches</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Recherche et évaluation de recruteurs</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Recherche de candidatures</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Alertes : mots-clés illimités et résultats illimités</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Matchmaking candidatures et missions : visibilité de tous les résultats</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00FF9D] mt-1">✓</span>
                  <span className="text-gray-400">Bookmarks de candidatures et missions préférées</span>
                </li>
              </ul>

              <a href="https://chromewebstore.google.com/detail/tarss-extension/cgdjipjplklipnlfbglgkjlnhfkpihfd?hl=fr" 
                 target="_blank" 
                 rel="noopener noreferrer" 
                 className="primary-button w-full flex items-center justify-center gap-2 px-6 py-3 text-base">
                Passer à Tarss+
              </a>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto mt-16">
            <h3 className="text-2xl font-bold mb-8 mono">Questions fréquentes</h3>
            <div className="space-y-6 text-left">
              <div>
                <h4 className="text-lg font-bold mb-2">Tarss est-il gratuit ?</h4>
                <p className="text-gray-400">Oui, Tarss propose une version gratuite avec l'essentiel des fonctionnalités. Tarss+ offre en complément des fonctionnalités avancées pour les utilisateurs qui souhaitent optimiser leur recherche de missions.</p>
              </div>
              <div>
                <h4 className="text-lg font-bold mb-2">Puis-je essayer Tarss+ avant de payer ?</h4>
                <p className="text-gray-400">Oui, vous pouvez commencer avec Tarss Gratuit et passer à Tarss+ quand vous le souhaitez. La version gratuite n'est pas limitée dans le temps.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 sm:py-12 px-4 sm:px-6 border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/planete-annelee.png" alt="Tarss" className="w-6 h-6" />
            <span className="font-bold mono">Tarss</span>
          </div>
          <div className="flex gap-4 items-center">
            <a 
              href="https://www.linkedin.com/in/pierre-dufour-662517102/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#00FF9D] hover:text-[#00FF9D]/80 transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <Link 
              to="/cgu" 
              className="text-gray-400 hover:text-[#00FF9D] transition-colors text-sm"
            >
              CGU
            </Link>
            <Link 
              to="/privacy" 
              className="text-gray-400 hover:text-[#00FF9D] transition-colors text-sm"
            >
              Confidentialité
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function createStars() {
  const starsContainer = document.createElement('div');
  starsContainer.className = 'stars';
  
  for (let i = 0; i < 50; i++) {
    const star = document.createElement('div');
    star.className = 'star';
    star.style.left = `${Math.random() * 100}%`;
    star.style.top = `${Math.random() * 100}%`;
    star.style.animationDelay = `${Math.random() * 4}s`;
    starsContainer.appendChild(star);
  }
  
  document.body.appendChild(starsContainer);
}